import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Box, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../components/StructuredData';

interface Experience {
  id: number;
  title: string;
  description: string;
  price: number;
  image: string;
  includes: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
}

const BookExperience: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const experience = location.state?.experience as Experience;
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const translatedIncludes = experience ? t(experience.includes, { returnObjects: true }) as string[] : [];

  if (!experience) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4">{t('booking.notFound')}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/experiences')}
          sx={{ mt: 2 }}
        >
          {t('booking.backToExperiences')}
        </Button>
      </Container>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};
    
    if (!formData.firstName.trim()) errors.firstName = t('booking.validation.firstNameRequired');
    if (!formData.lastName.trim()) errors.lastName = t('booking.validation.lastNameRequired');
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = t('booking.validation.emailRequired');
    } else if (!emailRegex.test(formData.email)) {
      errors.email = t('booking.validation.emailInvalid');
    }
    
    if (!formData.phone.trim()) errors.phone = t('booking.validation.phoneRequired');
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      const templateParams = {
        type: 'booking', 
        to_email: process.env.ADMIN_EMAIL,
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone,
        experience_title: t(experience.title), 
        experience_price: `€${experience.price}`,
        special_requests: formData.specialRequests || 'None',
        includes: translatedIncludes.join(', ')
      };

      fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateParams),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Failed to send email');
          setSnackbarMessage(t('booking.success'));
          setSnackbarSeverity('success');
          setSnackbarOpen(true);

          setTimeout(() => {
            navigate('/experiences');
          }, 2000);
        })
        .catch((error) => {
          console.error('Failed to send email:', error);
          setSnackbarMessage(t('booking.error'));
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Container sx={{ py: 8 }}>
      <SEO
        title={t('seo.booking.title')}
        description={t('seo.booking.description')}
        canonicalPath="/book-experience"
        jsonLd={[
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: t('experiences.title'), url: '/experiences' },
            { name: t('booking.title'), url: '/book-experience' },
          ]),
        ]}
      />

      <Typography variant="h4" component="h1" gutterBottom align="center">
        {t('booking.title')}
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardMedia
              component="img"
              height="200"
              image={experience.image}
              alt={t(experience.title)}
            />
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                {t(experience.title)}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t(experience.description)}
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                {t('common.currency')}{experience.price}
              </Typography>
              
              <Typography variant="subtitle1" component="h3" sx={{ mt: 2, mb: 1 }}>
                {t('booking.whatsIncluded')}
              </Typography>
              <List dense>
                {translatedIncludes.map((item, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('booking.yourInformation')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label={t('booking.firstName')}
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label={t('booking.lastName')}
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    name="email"
                    label={t('booking.email')}
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="phone"
                    name="phone"
                    label={t('booking.phone')}
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="specialRequests"
                    name="specialRequests"
                    label={t('booking.specialRequests')}
                    multiline
                    rows={4}
                    value={formData.specialRequests}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => navigate('/experiences')}
                      disabled={isSubmitting}
                    >
                      {t('booking.cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary" 
                      size="large"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isSubmitting ? t('booking.submitting') : t('booking.confirmBooking')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BookExperience;
